import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Box,
} from '@mui/material';
import { styled } from '@mui/styles';
import FolderSelector from './FolderSelector';
import CloseIcon from '@mui/icons-material/Close';

// ----------------------------
// Styled components
// ----------------------------
const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  alignItems: 'center',
}));

const Label = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.54)',
}));

const FolderSelectBox = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  flexBasis: '12em',
}));

const CenteredBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
}));

const DividerBox = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  gap: '0.5em',
  alignItems: 'center',
  margin: '1.5em 0',
  '& > hr': {
    flex: 1,
  }, 
}));

const ActionsBox = styled(DialogActions)(({ theme }) => ({
  marginTop: '2em',
}));

// ----------------------------
// Component
// ----------------------------
function DeleteFoldersDialog(props) {
  const {
    id,
    foldersMap,
    folderList,
    addFolder,
    handleDeleteSelectedAndDeleteItems,
    handleDeleteSelectedAndMoveItems,
    handleClose,
    open,
  } = props;

  const filtered = folderList.filter((f) => !foldersMap.hasOwnProperty(f.id));
  const [selectedFolder, setSelectedFolder] = useState(filtered.length > 0 ? filtered[0] : null);

  const itemCount = Object.values(foldersMap || {})
    .map((f) => f.item_count)
    .reduce((a, b) => a + b, 0);

  const folderIds = Object.values(foldersMap || {}).map((f) => f.id);

  const handleDeleteDestroy = () => {
    handleDeleteSelectedAndDeleteItems(folderIds);
    handleClose();
  };

  const handleDeleteMove = () => {
    if (!selectedFolder?.id) {
      return;
    }
    handleDeleteSelectedAndMoveItems(folderIds, selectedFolder.id);
    handleClose();
  };

  return !open || folderIds.length === 0 ? null : (
    <Dialog PaperProps={{ id }} onClose={handleClose} open={open} fullWidth maxWidth="sm">
      <DialogTitle>
        <Row sx={{ flexWrap: 'wrap-reverse', justifyContent: 'space-between' }}>
          Delete {folderIds.length} {folderIds.length > 1 ? 'Folders' : 'Folder'}
          <IconButton
            onClick={handleClose}
            sx={{ width: '2rem', height: '2rem', margin: '-0.5rem 0 0 auto' }}
          >
            <CloseIcon />
          </IconButton>
        </Row>
      </DialogTitle>

      <DialogContent>
        <DialogContentText id="moveFolderItemsMessage" sx={{ mb: 0 }}>
          Move {itemCount} items and Delete {folderIds.length}{' '}
          {folderIds.length > 1 ? 'folders' : 'folder'}
        </DialogContentText>

        <Row>
          <Label>Move To:</Label>
          <FolderSelectBox>
            <FolderSelector
              label="Folder"
              selectedFolder={selectedFolder}
              folderList={filtered}
              addFolder={addFolder}
              handleFolderSelect={setSelectedFolder}
            />
          </FolderSelectBox>
        </Row>

        <CenteredBox>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDeleteMove}
            disabled={filtered.length === 0}
          >
            Move Items and Delete Folders
          </Button>
        </CenteredBox>

        <DividerBox>
          <Divider />OR<Divider />
        </DividerBox>

        <DialogContentText id="deleteFolderItemsMessage">
          Delete {itemCount} items and Delete {folderIds.length}{' '}
          {folderIds.length > 1 ? 'folders' : 'folder'}
        </DialogContentText>

        <CenteredBox>
          <Button variant="contained" color="primary" onClick={handleDeleteDestroy}>
            Delete Items and Delete Folders
          </Button>
        </CenteredBox>
      </DialogContent>

      <ActionsBox>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </ActionsBox>
    </Dialog>
  );
}

export default DeleteFoldersDialog;
